import {uniqueId} from 'lodash';

import {CustomError} from '../../libs/shared/helpers/src/types/customErrors';
import {buildCancelTokenSource, CancelError, CancelToken} from './asyncCancelHelpers';

type PromiseResolve<T> = (value: T | PromiseLike<T>) => void;
type PromiseReject = (reason?: any) => void;

export class PromiseCompletionSource<T> {
  constructor() {
    let resolve: PromiseResolve<T> | null = null;
    let reject: PromiseReject | null = null;
    this.promise = new Promise<T>((promiseResolve, promiseReject) => {
      resolve = promiseResolve;
      reject = promiseReject;
    });

    if (!resolve || !reject) {
      throw new Error('Promise initialization failed');
    }

    this.resolve = resolve;
    this.reject = reject;
  }

  readonly promise: Promise<T>;
  readonly resolve: PromiseResolve<T>;
  readonly reject: PromiseReject;
}

export class CollectionSemaphore {
  constructor(items: Array<any>) {
    this.items = items;
    this.itemsCount = items.length;

    this.waiter = new Promise((resolve) => {
      this.resolver = resolve;
    });
  }

  private readonly items: Array<any>;
  private itemsCount: number;

  private readonly waiter: Promise<void>;
  private resolver?: () => void;

  removeItem(item: any) {
    // Make sure this item was part of the collection.
    if (this.items.includes(item)) {
      throw new Error('The provided item must be part of the collection.');
    }

    this.itemsCount--;

    // If we still have items left, nothing to do.
    if (this.itemsCount > 0 || !this.resolver) {
      return;
    }

    // Unblock all the waiters.
    this.resolver();
  }

  waitForAllRemovedAsync(): Promise<void> {
    return this.waiter;
  }
}

export class CounterSemaphore {
  constructor(count: number) {
    this.count = count;
    this.waiter = new Promise((resolve) => {
      this.resolver = resolve;
    });
  }

  private count: number;
  private readonly waiter: Promise<void>;
  private resolver?: () => void;

  waitAsync(): Promise<void> {
    if (this.count === 0 || !this.resolver) {
      throw new Error('This semaphore is already depleted.');
    }

    this.count--;
    if (this.count === 0) {
      this.resolver();
    }

    return this.waiter;
  }
}

export function delayAsync(delay: number = 0, cancelToken?: CancelToken): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(resolve, delay);

    if (cancelToken) {
      cancelToken.promise.then(() => {
        clearTimeout(timeoutId);
        reject(new CancelError());
      });
    }
  });
}

// Simpler version of lodash' DebouncedFunc without the confusing return types.
export interface DeferredFunc<T extends (this: unknown, ...args: any[]) => any> {
  (...args: Parameters<T>): void;
  cancel(): void;
  flush(): void;
}

/*
 * Timeout.
 */

type TimeoutCallback<T> = (elapsedWallTime: number) => T;

export class TimeoutError extends CustomError {
  constructor(elapsedWallTime: number) {
    super('Callback timed out');

    this.elapsedWallTime = elapsedWallTime;
  }

  readonly elapsedWallTime: number;
}

const defaultTimeoutHandler: TimeoutCallback<never> = (elapsedWallTime) => {
  throw new TimeoutError(elapsedWallTime);
};

/**
 * Imposes a timeout on an async operation.
 *
 * @param callback
 *   A callback that returns a promise for the async operation.
 * @param timeout
 *   The timeout in milliseconds.
 * @param timeoutCallback
 *   An optional callback that synchronously returns a fallback value. The default is to throw a TimeoutError.
 * @returns
 *   A promise that will settle to either the result of the operation callback, or the result of the timeout callback.
 */
export async function withTimeoutAsync<T>(
  callback: () => Promise<T>,
  timeout: number,
  timeoutCallback: TimeoutCallback<T> = defaultTimeoutHandler,
): Promise<T> {
  let isCompleted = false;
  const startWallTime = Date.now();
  const cancelTokenSource = buildCancelTokenSource();

  try {
    return await Promise.race([
      callback(),
      delayAsync(timeout, cancelTokenSource.token).then(async () => {
        if (isCompleted) {
          // The race has already completed, so our return value doesn't matter anymore.
          return undefined as any;
        }

        // The completion of the callback() promise may have already been scheduled on the event loop, such as in the
        // case of an IndexedDB operation, and the delayAsync() timer fired first only because it was scheduled first.
        // To avoid timing out due to a busy event loop, wait one more macrotask.
        await delayAsync(0);

        if (isCompleted) {
          // The race has already completed, so our return value doesn't matter anymore.
          return undefined as any;
        }

        const endWallTime = Date.now();
        return timeoutCallback(endWallTime - startWallTime);
      }),
    ]);
  } finally {
    cancelTokenSource.cancel();
    isCompleted = true;
  }
}

/*
 * Jitter.
 */

/** Jitters the given delay. A jitter factor of 0 means no jitter, 1 means full jitter. */
export function computeDelayWithJitter(delay: number, jitterWeight: number): number {
  if (jitterWeight < 0 || jitterWeight > 1) {
    throw new Error('Invalid jitter factor');
  }

  const nonJitterWeight = 1 - jitterWeight;
  const jitter = Math.random();

  return nonJitterWeight * delay + jitterWeight * delay * jitter;
}

export function delayWithJitterAsync(delay: number, jitterWeight: number, cancelToken?: CancelToken) {
  return delayAsync(computeDelayWithJitter(delay, jitterWeight), cancelToken);
}

/*
 * Buffer.
 */

export type AsyncBufferWaiter = Promise<{isLastWaiter: boolean}>;

export class AsyncBuffer {
  constructor() {
    this.waiterSource = new PromiseCompletionSource();
    this.wasFlushed = false;
  }

  private readonly waiterSource: PromiseCompletionSource<void>;
  private lastWaiterId?: string;
  private wasFlushed: boolean;

  async buildWaiter(): AsyncBufferWaiter {
    if (this.wasFlushed) {
      throw new Error('This buffer was already flushed.');
    }

    const waiterId = uniqueId('waiterid-');
    this.lastWaiterId = waiterId;

    await this.waiterSource.promise;
    return {isLastWaiter: waiterId === this.lastWaiterId};
  }

  flush() {
    if (this.wasFlushed) {
      throw new Error('This buffer was already flushed.');
    }

    this.wasFlushed = true;

    if (!this.lastWaiterId) {
      return;
    }

    this.waiterSource.resolve();
  }
}
